/* global mapboxgl, i18n */

import ApplicationController from "application_controller.js";
import { useDebounce } from "stimulus-use";
import { useI18n, useAssetLoader, useDecoder, useColorHelper, useLayerStyles, useMemberPopup, useGeojsonBounds, MEMBER_POPUP_CLASS_NAME } from "mixins";
import MapboxGLButtonControl from "../../mapbox";
import translations from "../../locales/view_components/map.json";

//* The place outlines drawn by mode "places": villages in the brand yellow,
//* beaches in the sky blue the rest of the site marks water with.
const PLACES_SOURCE_ID = "places";
const PLACE_VILLAGE_COLOR = "#FDD412";
const PLACE_BEACH_COLOR = "#38BDF8";

export default class extends ApplicationController  {
  debug = false;

  static values = {
    pageName: { type: String, default: "" },
    mode: { type: String, default: "default" },
    mapboxUsername: String,
    mapboxStyleId: String,
    mapboxAccessToken: String,
    latitude: String,
    longitude: String,
    //* Mirrors Gt::Geolocation::DEFAULT_MAP_ZOOM, which is what Map::Component emits; keep the two in step.
    zoom: { type: Number, default: 14 },

    //* Options for Marker
    markerName: { type: String, default: "pin-l" },
    markerLabel: { type: String, default: "" },
    markerColor: { type: String, default: "#3FB1CE" },
    markerContent: { type: String, default: "" },
    markerAnchor: { type: String, default: "center" },
    markerScale: { type: Number, default: 1 },
    markerIcon: { type: String, default: "" },
    markerIconColor: { type: String, default: "auto" },

    //* Options for popup
    popupContent: { type: String, default: "" },
    popupAnchor: { type: String, default: "bottom" },
    popupClassName: { type: String, default: "" },
    popupCloseButton: { type: Boolean, default: true },
    popupCloseOnClick: { type: Boolean, default: true },
    popupCloseOnMove: { type: Boolean, default: false },
    popupFocusAfterOpen: { type: Boolean, default: false },
    popupMaxWidth: { type: String, default: "240px" },
    popupOffset: { type: Number, default: 0 },

    //* Options for POI button
    iconPoiEnable: { type: String, default: "" },
    iconPoiDisable: { type: String, default: "" },

    //* Options for POI layer
    withPoi: { type: Boolean, default: true },
    poiVisible: { type: Boolean, default: true },

    //* Options for Custom Markers
    accommodationId: { type: String, default: "" },

    //* Options for Member Markers (mode "members")
    membersUrl: { type: String, default: "" },

    //* Options for Place Outlines (mode "places")
    placesUrl: { type: String, default: "" },
  };

  static targets = [ "cta", "static_map", "interactive_map" ];

  static classes = [ "loading", "loaded" ];

  static debounces = [ "checkzoom", "checkFullscreen" ];

  connect() {
    this.log_function("connect");

    useDebounce(this);
    useColorHelper(this);
    useAssetLoader(this);
    useDecoder(this);
    useI18n({
      controller: this,
      translations: translations,
      scope: "view_components.map",
    });
    useLayerStyles(this);
    useMemberPopup(this);
    useGeojsonBounds(this);

    this.allLayerIds = [];
    this.poisVisible = true;

    this.mapAPIloaded = false;
    this.map = false;

    this.mapEvents = [];

    switch (this.modeValue) {
      case "static":
        this.loadStaticMap();
        this.ctaTarget.style.display = "none";
        this.element.style.cursor = "default";
        break;
      case "interactive":
        this.element.addEventListener("lazybeforeunveil", this.loadInteractiveMap.bind(this), { once : true });
        break;
      case "members":
        //* The members map lives in the listing's view-toggle overlay: load right away
        //* when it starts visible (?view=map), otherwise when the toggle first shows it
        this.ctaTarget.style.display = "none";
        this.element.style.cursor = "default";
        this.boundMembersUpdate = this.updateMembers.bind(this);
        window.addEventListener("members-map:update", this.boundMembersUpdate);
        if (this.element.offsetParent) {
          this.loadInteractiveMap();
        } else {
          this.boundMembersActivate = this.loadInteractiveMap.bind(this);
          window.addEventListener("members-map:activate", this.boundMembersActivate, { once : true });
        }
        break;
      case "places":
        //* Same overlay lifecycle as the members map, drawing outlines rather
        //* than markers (see loadPlaces)
        this.ctaTarget.style.display = "none";
        this.element.style.cursor = "default";
        this.boundPlacesUpdate = this.updatePlaces.bind(this);
        window.addEventListener("places-map:update", this.boundPlacesUpdate);
        if (this.element.offsetParent) {
          this.loadInteractiveMap();
        } else {
          this.boundPlacesActivate = this.loadInteractiveMap.bind(this);
          window.addEventListener("places-map:activate", this.boundPlacesActivate, { once : true });
        }
        break;
      default:
        this.loadStaticMap();
        this.element.addEventListener("click", this.loadInteractiveMap.bind(this), { once : true });
        break;
    }
  }

  get marker_color() {
    this.log_function("get marker_color");

    //* This switch checks where to get the color value from
    switch (true) { //* if you don't understand why it is set to true, read this https://seanbarry.dev/posts/switch-true-pattern
      case this.markerColorValue.startsWith("#"): //* If the string starts with a #, it must be a HEX color code.
        return this.markerColorValue; //* Get the color from  the provided string
      case this.markerColorValue.startsWith("--"): //* If the string starts with a --, it must be a css variable.
        return getComputedStyle(document.documentElement).getPropertyValue(this.markerColorValue).trim(); //* Get the color value from the CSS variable
      default: //* If the string does not start with -- or # send a default color
        return "#FF0000"; //* Send a default color #FF0000 (red)
    }
    //* If you don't understand this code just ask Anthony
    //* If you are Anthony and still don't understand this code past Anthony is laughing now!
  }

  get marker_icon_color() {
    this.log_function("get marker_icon_color");

    if (this.markerIconColorValue === "auto") {
      //! We don't have the logic that MapBox uses so it's a bit of trial and error
      //* We adjust the threshold value as we test,
      //* We want to the static map marker icon and the interactive map marker icons to match
      return this.hex_contrasted_color(this.marker_color, { threshold: 150 });
    }

    //* If this.markerIconColorValue is not se to auto just return the color that it was set to
    return this.markerIconColorValue;
  }

  loadStaticMap() {
    this.log_function("loadStaticMap");

    //* If needing to load a custom marker icon in static map, refer to this documentation: https://docs.mapbox.com/api/maps/static-images/#example-request-retrieve-a-static-map-with-a-custom-marker-overlay

    let map_width = this.static_mapTarget.clientWidth;
    let map_height = this.static_mapTarget.clientHeight;
    const map_ratio = map_width / map_height;
    const max_size = 1280; //* max size of the map provided by mapbox in pixels (width or height)

    //* Mapbox requires an integer that is why Math.floor is used
    if(map_width > max_size || map_height > max_size) {
      if (map_width > map_height) {
        map_width = max_size;
        map_height = Math.floor(map_width / map_ratio);
      } else if (map_height > map_width) {
        map_height = max_size;
        map_width = Math.floor(map_height / map_ratio);
      } else {
        map_width = max_size;
        map_height = max_size;
      }
    }

    const static_map_dimensions = `${map_width}x${map_height}`;
    const username = this.mapboxUsernameValue;
    const style_id = this.mapboxStyleIdValue;
    const access_token = this.mapboxAccessTokenValue;

    //* Marker shape and size. Options are pin-s and pin-l.
    const marker_name = this.markerNameValue;

    //* Optional. Marker symbol. Options are a lowercase alphanumeric label a through z, 0 through 99, or a valid Maki icon. If a letter is requested, it will be rendered in uppercase only.
    const marker_label = this.markerLabelValue ? `-${this.markerLabelValue}` : "";

    //* Optional. A 3-digit or 6-digit HEX color code.
    const marker_color_no_alpha = this.hex_no_alpha(this.marker_color).replace("#", "").toLowerCase();

    //* The location at which to center the marker. When using an asymmetric marker, make sure that the tip of the pin is at the center of the image.
    const marker_longitude = this.longitudeValue;
    const marker_latitude = this.latitudeValue;

    const markerUrlParam = `${marker_name}${marker_label}+${marker_color_no_alpha}(${marker_longitude},${marker_latitude})/`;

    const src = `https://api.mapbox.com/styles/v1/${username}/${style_id}/static/${markerUrlParam}${this.longitudeValue},${this.latitudeValue},${this.zoomValue},0/${static_map_dimensions}@2x?access_token=${access_token}&logo=true&attribution=true`;

    this.static_mapTarget.setAttribute("data-src", src);
    this.static_mapTarget.classList.add("lazyload");
  }

  loadInteractiveMap() {
    this.log_function("loadInteractiveMap");

    this.element.classList.add(this.loadingClass);

    //* "https://api.mapbox.com/mapbox-gl-js/v2.9.2/mapbox-gl.css"
    this.load_style("/node_modules/mapbox-gl/dist/mapbox-gl.css");

    //* "https://api.mapbox.com/mapbox-gl-js/v2.9.2/mapbox-gl.js"
    this.load_script("/node_modules/mapbox-gl/dist/mapbox-gl.js", this.generateMap.bind(this) );

    this.element.removeEventListener("click", this.loadInteractiveMap.bind(this));
    this.element.removeEventListener("lazybeforeunveil", this.loadInteractiveMap.bind(this));

  }

  generateMap() {
    this.log_function("generateMap");

    const markerIcon = this.markerIconValue;
    const markerColor = this.marker_color;
    const markerIconColor = this.marker_icon_color;
    const styleForMarkerCircle = markerIcon ? "display: none;" : "";
    this.interactive_mapTarget.style.display = "block";
    this.mapAPIloaded = true;
    this.foundArea51 = false;

    const pinHtml =
      `<div aria-label="Map marker">
        <div style="margin-bottom: 52.5px;">
          <svg display="block" height="61.5px" width="40.5px" viewBox="0 0 40.5 61.5">
            <defs>
              <radialGradient id="shadowGradient">
                <stop offset="10%" stop-opacity="0.4"></stop>
                <stop offset="100%" stop-opacity="0.05"></stop>
              </radialGradient>
            </defs>
            <ellipse cx="20.25" cy="52.2" rx="15.75" ry="7.875" fill="url(#shadowGradient)"></ellipse>
            <path fill="${markerColor}" d="M40.5,20.25C40.5,28.605 30.375,40.5 22.125,51.75C21.03,53.25 19.47,53.25 18.375,51.75C10.125,40.5 0,28.83 0,20.25C0,9.06 9.06,0 20.25,0C31.44,0 40.5,9.06 40.5,20.25Z"></path>
            <path opacity="0.25" d="M20.25,0C9.06,0 0,9.06 0,20.25C0,28.83 10.125,40.5 18.375,51.75C19.5,53.28 21.03,53.25 22.125,51.75C30.375,40.5 40.5,28.605 40.5,20.25C40.5,9.06 31.44,0 20.25,0ZM20.25,1.5C30.63,1.5 39,9.87 39,20.25C39,23.85 36.75,28.77 33.33,34.11C29.925,39.45 25.065,45.21 20.91,50.865C20.61,51.27 20.415,51.48 20.25,51.66C20.085,51.48 19.89,51.27 19.59,50.865C15.42,45.195 11.115,39.465 7.53,34.155C3.93,28.845 1.5,23.925 1.5,20.25C1.5,9.87 9.87,1.5 20.25,1.5Z"></path>
            <circle fill="white" cx="20.25" cy="20.25" r="8.25" style="${styleForMarkerCircle}"></circle>
          </svg>
        </div>
        <div style="position: absolute; top: 50%; left:50%; transform: translate(-50%, -50%); margin-top: -36px; fill: ${markerIconColor}; font-size: 1em;">
          <div style="scale: 1.5">
            ${markerIcon}
          </div>
        </div>
      </div>`;

    this.map = new mapboxgl.Map({
      accessToken: this.mapboxAccessTokenValue,
      container: this.interactive_mapTarget,
      zoom: this.zoomValue,
      // maxZoom: 15,
      center: [this.longitudeValue, this.latitudeValue],
      style: `mapbox://styles/${this.mapboxUsernameValue}/${this.mapboxStyleIdValue}`,
      exitFullscreenOnEsc: true,
      locale: {
        "AttributionControl.MapFeedback": this.t("controls.attribution.feedback"),
        "AttributionControl.ToggleAttribution": this.t("controls.attribution.toggle"),
        "FullscreenControl.Enter": this.t("controls.fullscreen.enter"),
        "FullscreenControl.Exit": this.t("controls.fullscreen.exit"),
        "GeolocateControl.FindMyLocation": this.t("controls.geolocate.find_my_location"),
        "GeolocateControl.LocationNotAvailable": this.t("controls.geolocate.location_not_available"),
        "LogoControl.Title": this.t("controls.logo.title"),
        "Map.Title": this.t("title"),
        "NavigationControl.ResetBearing": this.t("controls.navigation.reset_bearing"),
        "NavigationControl.ZoomIn": this.t("controls.navigation.zoom_in"),
        "NavigationControl.ZoomOut": this.t("controls.navigation.zoom_out"),
        "ScrollZoomBlocker.CmdMessage": this.t("scroll_zoom_blocker.cmd_message"),
        "ScrollZoomBlocker.CtrlMessage": this.t("scroll_zoom_blocker.ctrl_message"),
        "TouchPanBlocker.Message": this.t("touch_pan_blocker.message"),
      },
    });

    // this.map._logoControl._container.style.scale = 0.5;
    //* A members or places map renders its own layers instead of the single marker
    if (!this.membersUrlValue && !this.placesUrlValue) {
      const marker = new mapboxgl.Marker({
        element: this.decodeHtml(this.markerContentValue || pinHtml),
        color: this.marker_color,
        anchor: this.markerAnchorValue,
        scale: this.markerScaleValue,
      })
      .setLngLat([this.longitudeValue, this.latitudeValue])
      .addTo(this.map);

      if(this.popupContentValue) {
        const popup = new mapboxgl.Popup({
          anchor: this.popupAnchorValue,
          className: this.popupClassNameValue,
          closeButton: this.popupCloseButtonValue,
          closeOnClick: this.popupCloseOnClickValue,
          closeOnMove: this.popupCloseOnMoveValue,
          focusAfterOpen: this.popupFocusAfterOpenValue,
          maxWidth: this.popupMaxWidthValue,
          offset: this.popupOffsetValue,
        }).setHTML(this.popupContentValue);
        marker.setPopup(popup).togglePopup();
      }
    }

    const navigationControlOptions = {
      showCompass: false,
      showZoom: true,
      visualizePitch: true,
    };

    const centerMap = () => {
      this.map.flyTo({
        center: [this.longitudeValue, this.latitudeValue],
        zoom: this.zoomValue,
        pitch: 0,
        bearing: 0,
      });

      const centeredMap = "clicked centered map";
      if(!this.checkMapEventUsed(centeredMap)){
        this.updateMapEvents(centeredMap);
        this.dispatch("ahoy_trackEvent", { detail: { type: "ux", location: "map", action: centeredMap, page: this.pageNameValue }, prefix: false });
      }
    };

    const togglePoiMarkers = () => {
      let poiVisibility = false;

      this.allLayerIds.forEach((id) => {
        const visibility = this.map.getLayoutProperty(id, "visibility") || "visible";
        if (visibility === "visible") {
          this.map.setLayoutProperty(id, "visibility", "none");
          poiVisibility = "clicked hide POIs";
          this.poisVisible = false;
        } else {
          this.map.setLayoutProperty(id, "visibility", "visible");
          poiVisibility = "clicked show POIs";
          this.poisVisible = true;
        }
      });

      if(poiVisibility && !this.checkMapEventUsed(poiVisibility)){
        this.updateMapEvents(poiVisibility);
        this.dispatch("ahoy_trackEvent", { detail: { type: "ux", location: "map", action: poiVisibility, page: this.pageNameValue }, prefix: false });
      }
    };

    const ctrlPois = new MapboxGLButtonControl({
      className: "mapboxgl-ctrl-pois",
      toggleOnLabel: `${this.iconPoiEnableValue} ${this.t("controls.poi.hide")}`,
      toggleOffLabel: `${this.iconPoiDisableValue}  ${this.t("controls.poi.show")}`,
      title: `${this.t("controls.poi.title")}`,
      eventHandler: togglePoiMarkers,
      toggleState: this.poiVisibleValue,
    });

    const ctrlCenter = new MapboxGLButtonControl({
      className: "mapboxgl-ctrl-home",
      title: `${this.t("controls.center.title")}`,
      eventHandler: centerMap,
    });

    //* If a map scale indicator is required, uncomment the code below
    // const scale = new mapboxgl.ScaleControl({
    //   maxWidth: 200,
    //   unit: "metric",
    // });
    // this.map.addControl(scale);
    this.map.addControl(new mapboxgl.NavigationControl(navigationControlOptions), "bottom-right");
    this.map.addControl(ctrlCenter, "bottom-right");
    if (this.withPoiValue) {
      //* On the members map, narrow screens push this below the overlaid view
      //* toggle via CSS (see .members-map-view .mapboxgl-ctrl-top-left)
      this.map.addControl(ctrlPois, "top-left");
    }
    this.map.addControl(new mapboxgl.FullscreenControl(), "top-right");

    this.map.on("load", () => {
      //* POI layer: all published non-private places (togglable)
      if (this.withPoiValue) {
        //* This request get ALL map markers (added to pois layer)
        this.map.addSource("pois", {
          "type": "geojson",
        //* Use a URL for the value for the `data` property.
          "data": i18n._locale === "en" ? "/markers.json" : `/${i18n._locale}/markers.json`,
        });


        //* There are 2 separate layers in order to keep custom markers from
        //* being hidden when the show/hide POIs toggle button is clicked.
        //* These layers provided by useLayerStyles mixin.

        this.addMapStyleLayer(this.polygonLayer("pois", "pois-polygon"), true);
        this.addMapStyleLayer(this.outlineLayer("pois", "pois-outline"), true);
        this.addMapStyleLayer(this.pointLayer("pois", "pois-point"), true);
        this.addMapStyleLayer(this.polygonLabelLayer("pois", "pois-polygon-label"), true);
        this.addMapStyleLayer(this.pathLabelLayer("pois", "pois-path-label"), true);

        //* POIs start hidden when asked to (poi_visible: false); the POI control
        //* (constructed with the matching toggleState) shows them on demand
        if (!this.poiVisibleValue) {
          this.allLayerIds.forEach((id) => this.map.setLayoutProperty(id, "visibility", "none"));
          this.poisVisible = false;
        }
      }

      //* There are 2 separate layers in order to keep custom markers from
      //* being hidden when the show/hide POIs toggle button is clicked.
      //* This request gets markers associated with a SPECIFIC listing (added to custom_markers layer)
      //* There are 2 separate layers in order to keep custom markers from
      //* being hidden when the show/hide POIs toggle button is clicked.
      if (this.accommodationIdValue) {
        this.map.addSource("custom_markers", {
          "type": "geojson",
          "data": i18n._locale === "en" ? `/markers.json?accommodation_id=${this.accommodationIdValue}` : `/${i18n._locale}/markers.json?accommodation_id=${this.accommodationIdValue}`,
        });

        this.addMapStyleLayer(this.polygonLayer("custom_markers", "custom-polygon"));
        this.addMapStyleLayer(this.outlineLayer("custom_markers", "custom-outline"));
        this.addMapStyleLayer(this.pointLayer("custom_markers", "custom-point"));
        this.addMapStyleLayer(this.polygonLabelLayer("custom_markers", "custom-polygon-label"));
        this.addMapStyleLayer(this.pathLabelLayer("custom_markers", "custom-path-label"));
      }

      //* Clustered member markers for the listing map view
      if (this.membersUrlValue) {
        this.loadMembers();
      }

      if (this.placesUrlValue) {
        this.loadPlaces();
      }
    });

    this.element.classList.add(this.loadedClass);

    this.interactiveMapLoadedTimeout = setTimeout(() => {
      this.static_mapTarget.style.display = "none";
      this.element.classList.remove(this.loadingClass);
    }, 2000);

    this.updateLastZoom();

    this.map.on("zoom", (event) => this.checkzoom(event) );

    this.fullScreenChange = false;
    if ("onfullscreenchange" in window.document) {
      this.fullScreenChange = "fullscreenchange";
    } else if ("onmozfullscreenchange" in window.document) {
      this.fullScreenChange = "mozfullscreenchange";
    } else if ("onwebkitfullscreenchange" in window.document) {
      this.fullScreenChange = "webkitfullscreenchange";
    } else if ("onmsfullscreenchange" in window.document) {
      this.fullScreenChange = "MSFullscreenChange";
    }

    if (this.fullScreenChange) {
      this.boundCheckFullscreen = this.checkFullscreen.bind(this);
      window.document.addEventListener(this.fullScreenChange, this.boundCheckFullscreen);
    }

    //* Disables map rotation using right click + drag
    this.map.dragRotate.disable();

    //* Disables map rotation using touch rotation gesture
    this.map.touchZoomRotate.disableRotation();

    //* Disables map tilting using two-finger gesture
    this.map.touchPitch.disable();

    //* Disables the "box zoom" interaction.
    this.map.boxZoom.disable();

    this.map.on("moveend", (event) => {
      const currentBoundingBox = new mapboxgl.LngLatBounds(this.map.getBounds().toArray());
      const area51Location = new mapboxgl.LngLat(-115.812251, 37.234729);

      if(this.poisVisible && !this.foundArea51 && currentBoundingBox.contains(area51Location)){
        this.dispatch("ahoy_trackEvent", { detail: { type: "ux", location: "map", action: "found Area51", page: this.pageNameValue }, prefix: false });
        this.foundArea51 = true;

        this.confettiElement = document.createElement("div"); //* Create a new div element
        this.confettiElement.classList.add("position-absolute", "top-50", "start-50", "translate-middle"); //* Add classes to the div element
        this.confettiElement.setAttribute("data-controller", "confetti"); //* Set the data-controller attribute
        this.element.appendChild(this.confettiElement); //* Append the div element to the body or another element in the document

        this.confettiElementRemoveTimeout = setTimeout(() => {
          this.confettiElement.remove();
        }, 5000);
      }
    });
  }

  addMapStyleLayer(attributes = {}, add_to_layers_array = false){
    this.map.addLayer(attributes);

    if(add_to_layers_array){
      const { id } = attributes;
      this.allLayerIds.push(id);
    }
  }

  //* The places map view: fetches every published village and beach as a
  //* GeoJSON FeatureCollection of polygons and draws them as outlines with the
  //* place's name over them. Clicking one opens the member popup — the same
  //* card the listing map opens, so a place and a hotel read alike.
  async loadPlaces() {
    this.log_function("loadPlaces");

    let geojson;
    try {
      const response = await fetch(this.placesUrlValue, { headers: { "Accept": "application/json" } });
      geojson = await response.json();
    } catch {
      return;
    }

    this.map.addSource(PLACES_SOURCE_ID, { type: "geojson", data: geojson });

    //* Villages in the brand yellow, beaches in the sky blue the rest of the
    //* site marks water with, both keyed off the feature's own kind
    const kindColor = ["match", ["get", "kind"], "village", PLACE_VILLAGE_COLOR, PLACE_BEACH_COLOR];

    this.map.addLayer({
      "id": `${PLACES_SOURCE_ID}-fill`,
      "type": "fill",
      "source": PLACES_SOURCE_ID,
      "paint": { "fill-color": kindColor, "fill-opacity": 0.18 },
    });

    this.map.addLayer({
      "id": `${PLACES_SOURCE_ID}-line`,
      "type": "line",
      "source": PLACES_SOURCE_ID,
      "paint": { "line-color": kindColor, "line-width": 2 },
    });

    //* The name sits at the shape's centre. text-allow-overlap stays off so
    //* Mapbox drops the labels that would collide as the visitor zooms out.
    this.map.addLayer({
      "id": `${PLACES_SOURCE_ID}-label`,
      "type": "symbol",
      "source": PLACES_SOURCE_ID,
      "layout": {
        "text-field": ["get", "label_title"],
        "text-size": 14,
        "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
        "text-allow-overlap": false,
      },
      "paint": {
        "text-color": "#FFFFFF",
        "text-halo-color": "#000000",
        "text-halo-width": 1.5,
      },
    });

    this.fitPlacesBounds(geojson);

    [`${PLACES_SOURCE_ID}-fill`, `${PLACES_SOURCE_ID}-label`].forEach((layerId) => {
      this.map.on("click", layerId, (event) => this.openPlacePopup(event));
      this.map.on("mouseenter", layerId, () => { this.map.getCanvas().style.cursor = "pointer"; });
      this.map.on("mouseleave", layerId, () => { this.map.getCanvas().style.cursor = ""; });
    });
  }

  //* In-place refresh from the view toggle's kind filter: swap the source data,
  //* no map teardown. Before the source exists (map still initializing) only the
  //* fetch URL updates, so the initial load draws the chosen kind.
  //*
  //* The frame is left where the visitor put it. Every places response names the
  //* same box — the island's, whatever the filter (see the bbox note on the
  //* response) — so refitting could only throw away a zoom, never improve one.
  updatePlaces(event) {
    this.log_function("updatePlaces");

    const { geojson, url } = event.detail;
    this.placesUrlValue = url;

    const source = this.map && this.map.getSource && this.map.getSource(PLACES_SOURCE_ID);
    if (!source) return;

    //* The open popup belongs to a place the new set may not hold
    this.placePopup?.remove();
    this.placePopup = null;

    source.setData(geojson);
  }

  //* Opens the popup over the point that was clicked rather than the shape's
  //* centre: a beach is a long thin ribbon, and a card anchored to its middle
  //* can land well away from where the visitor pressed.
  //*
  //* Beaches sit on the shore of the villages that hold them, so a click often
  //* lands inside two outlines at once. The smallest one wins — it is the more
  //* particular answer to where the visitor pressed, and the larger place is a
  //* click away in the popup's own page.
  openPlacePopup(event) {
    this.log_function("openPlacePopup");

    const feature = event.features?.slice().sort((a, b) => this.placeArea(a) - this.placeArea(b))[0];
    if (!feature) return;

    this.placePopup?.remove();
    this.placePopup = new mapboxgl.Popup({ maxWidth: "260px", className: MEMBER_POPUP_CLASS_NAME })
      .setLngLat(event.lngLat)
      .setHTML(this.memberPopupHtml(feature.properties))
      .addTo(this.map);
  }

  //* A feature's bounding-box area, for ranking overlapping outlines. Good
  //* enough to tell a beach from the village around it, and it needs no
  //* geometry library.
  placeArea(feature) {
    const bounds = this.geojsonBounds({ features: [feature] });
    if (!bounds) return Infinity;

    const [[west, south], [east, north]] = bounds;
    return (east - west) * (north - south);
  }

  //* Frames the island, or every outline on the page when the response names
  //* no box of its own (see the bbox note on the places response)
  fitPlacesBounds(geojson) {
    this.log_function("fitPlacesBounds");

    const box = geojson.bbox;
    const bounds = box ? [[box[0], box[1]], [box[2], box[3]]] : this.geojsonBounds(geojson);
    if (!bounds) return;

    //* A phone has no room to spare: the desktop's generous margin around the
    //* island leaves it too small to read the labels on
    const padding = this.map.getContainer().clientWidth < 576 ? 16 : 64;
    this.map.fitBounds(bounds, { padding });
  }

  //* The listing map view: fetches the full filtered member set (a GeoJSON
  //* FeatureCollection) and renders it as clustered brand-colored markers.
  //* Clicking a cluster zooms into it; clicking a member opens its popup.
  async loadMembers() {
    this.log_function("loadMembers");

    let geojson;
    try {
      const response = await fetch(this.membersUrlValue, { headers: { "Accept": "application/json" } });
      geojson = await response.json();
    } catch {
      return;
    }

    this.map.addSource("members", {
      "type": "geojson",
      "data": geojson,
    });

    //* Stacks pins the way the list sorts members: match score first, then paid
    //* tier (tier_rank 1 = gold … 4 = basic). Higher sort keys draw on top and
    //* win label collisions; the tier component stays below the 10-point score
    //* steps so it only breaks score ties.
    const memberSortKey = [
      "+",
      ["*", ["coalesce", ["get", "match_score"], 0], 10],
      ["-", 5, ["coalesce", ["get", "tier_rank"], 5]],
    ];

    //* Ranked pins follow the match ring's color scale (see the
    //* member-match-ring tiers): teal at 100%, green from 3/4, yellow from 1/2,
    //* orange from 1/4, red below. Members without ranking keep the brand pin.
    const matchRatio = ["/", ["get", "match_score"], ["get", "match_max"]];

    this.addMemberPinImages(geojson);

    //* The score label is BAKED INTO the pin image (one image per occurring
    //* percent, color by tier): Mapbox paints a symbol layer's text in a pass
    //* above its icons, so any real text — however sorted — could float over a
    //* pin that covers its own. Pixels in the icon cannot.
    this.map.addLayer({
      "id": "members-point",
      "type": "symbol",
      "source": "members",
      "layout": {
        "icon-image": [
          "case",
          ["!", ["has", "match_max"]], "member-pin-default",
          ["concat", "member-pin-p", ["to-string", ["round", ["*", matchRatio, 100]]]],
        ],
        "icon-anchor": "bottom",
        "icon-allow-overlap": true,
        //* Ranked pins grow to make room for their score label in the head
        "icon-size": ["case", ["has", "match_max"], 1, 0.6],
        "symbol-sort-key": memberSortKey,
      },
    });

    this.fitMembersBounds(geojson);

    this.map.on("click", "members-point", (event) => {
      const feature = event.features[0];
      //* The offset clears the pin, which stands ~54px above its anchor point
      this.memberPopup?.remove();
      this.memberPopup = new mapboxgl.Popup({ anchor: "bottom", offset: 58, maxWidth: "260px", className: MEMBER_POPUP_CLASS_NAME })
        .setLngLat(feature.geometry.coordinates)
        .setHTML(this.memberPopupHtml(feature.properties))
        .addTo(this.map);
      //* Bring the marker (and the popup opening above it) clear of the overlaid controls
      this.map.easeTo({ center: feature.geometry.coordinates });
    });

    this.map.on("mouseenter", "members-point", () => { this.map.getCanvas().style.cursor = "pointer"; });
    this.map.on("mouseleave", "members-point", () => { this.map.getCanvas().style.cursor = ""; });
  }

  //* In-place refresh from the view toggle's filter submission: swap the source
  //* data and reframe, no map teardown. Before the source exists (map still
  //* initializing) only the fetch URL updates, so the initial load is filtered.
  updateMembers(event) {
    this.log_function("updateMembers");

    const { geojson, url } = event.detail;
    this.membersUrlValue = url;

    const source = this.map && this.map.getSource && this.map.getSource("members");
    if (!source) return;

    //* The previously selected member's popup belongs to the old result set
    this.memberPopup?.remove();
    this.memberPopup = null;

    //* New filter combinations can produce percents without a pin image yet
    this.addMemberPinImages(geojson);
    source.setData(geojson);
    this.fitMembersBounds(geojson);
  }

  //* Registers the pin images the current member set needs: the plain brand pin
  //* plus one per occurring match percent, colored by the match ring's tier
  //* scale and with the score label baked into the pin head (see the layer
  //* comment in loadMembers for why the label is part of the image)
  addMemberPinImages(geojson) {
    this.log_function("addMemberPinImages");

    if (!this.map.hasImage("member-pin-default")) {
      this.map.addImage("member-pin-default", this.memberPinImage("#FDD412", "#003366"), { pixelRatio: 2 });
    }

    const percents = new Set();
    geojson.features.forEach((feature) => {
      const { match_score, match_max } = feature.properties;
      if (match_max != null) percents.add(Math.round((match_score / match_max) * 100));
    });

    percents.forEach((percent) => {
      const name = `member-pin-p${percent}`;
      if (this.map.hasImage(name)) return;

      //* Same thresholds as the member-match-ring tiers
      const fill = percent >= 100 ? "#20C997"
        : percent >= 75 ? "#198754"
        : percent >= 50 ? "#FFC107"
        : percent >= 25 ? "#FD7E14"
        : "#DC3545";
      this.map.addImage(name, this.memberPinImage(fill, "#FFFFFF", `${percent}%`), { pixelRatio: 2 });
    });
  }

  memberPinImage(fill, outline, label = "") {
    //* The teardrop path from this controller's single-marker pin (40.5 x 53.25)
    const pin = new Path2D("M40.5,20.25C40.5,28.605 30.375,40.5 22.125,51.75C21.03,53.25 19.47,53.25 18.375,51.75C10.125,40.5 0,28.83 0,20.25C0,9.06 9.06,0 20.25,0C31.44,0 40.5,9.06 40.5,20.25Z");
    const scale = 2;
    const padding = 2;
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil((40.5 + padding * 2) * scale);
    canvas.height = Math.ceil((53.25 + padding * 2) * scale);

    const context = canvas.getContext("2d");
    context.scale(scale, scale);
    context.translate(padding, padding);
    context.fillStyle = fill;
    context.fill(pin);
    context.lineWidth = 2;
    context.strokeStyle = outline;
    context.stroke(pin);

    if (label) {
      //* Centered on the pin head, white with a navy halo so it also reads on
      //* the light tiers (yellow, orange)
      context.font = "700 12px 'Open Sans', Helvetica, sans-serif";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.lineWidth = 3;
      context.strokeStyle = "#003366";
      context.strokeText(label, 20.25, 20.25);
      context.fillStyle = "#FFFFFF";
      context.fillText(label, 20.25, 20.25);
    }

    return context.getImageData(0, 0, canvas.width, canvas.height);
  }

  //* Frame every member; falls back to the map's initial center/zoom when the
  //* filtered set is empty
  fitMembersBounds(geojson) {
    this.log_function("fitMembersBounds");

    const coordinates = geojson.features
      .filter((feature) => feature.geometry.type === "Point")
      .map((feature) => feature.geometry.coordinates);
    if (!coordinates.length) return;

    const bounds = coordinates.reduce(
      (accumulated, coordinate) => accumulated.extend(coordinate),
      new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
    );
    this.map.fitBounds(bounds, { padding: 64, maxZoom: 15 });
  }

  updateCurrentZoom(){
    this.log_function("updateCurrentZoom");

    this.currentZoom = this.map.getZoom();
  }

  updateLastZoom(){
    this.log_function("updateLastZoom");

    this.lastZoom = this.map.getZoom();
  }

  checkzoom(event){
    this.log_function("checkzoom");

    this.updateCurrentZoom();

    let used_type = false;
    if (event.originalEvent) {
      switch (event.originalEvent.type) {
        case "click":
          used_type = "clicked on button";
          break;
        case "touchstart", "touchend":
          used_type = "pinched on touchscreen";
          break;
        case "wheel":
          used_type = "scrolled on mouse";
          break;
        case "keyup", "keydown":
          used_type = "typed on keyboard";
          break;
        default:
          used_type = "method undetected";
          break;
      }
    }

    let zoom = false; // eslint-disable-line no-useless-assignment
    switch (true) {
      case this.currentZoom > this.lastZoom:
        zoom = "zoom in";
        break;
      case this.currentZoom < this.lastZoom:
        zoom = "zoom out";
        break;
      default:
        zoom = "zoom none";
        break;
    }

    if(zoom && used_type) {
      const zoomUsed = `${used_type} | ${zoom}`;
      if(!this.checkMapEventUsed(zoomUsed)){
        this.updateMapEvents(zoomUsed);
        this.dispatch("ahoy_trackEvent", { detail: { type: "ux", location: "map", action: zoomUsed, page: this.pageNameValue }, prefix: false });
      }
    }

    this.updateLastZoom();
  }

  checkFullscreen(){
    this.log_function("checkFullscreen");

    let fullscreen = false;
    if(document.fullscreenElement && document.fullscreenElement.classList.contains("mapboxgl-map")){
      this.mapIsFullscreen = true;
      fullscreen = "entered fullscreen";
    } else if(this.mapIsFullscreen) {
      fullscreen = "exited fullscreen";
      this.mapIsFullscreen = false;
    }

    if(fullscreen) {
      if(!this.checkMapEventUsed(fullscreen)){
        this.updateMapEvents(fullscreen);
        this.dispatch("ahoy_trackEvent", { detail: { type: "ux", location: "map", action: fullscreen, page: this.pageNameValue }, prefix: false });
      }
    }
  }


  disconnect(){
    this.log_function("disconnect");

    if (this.mapAPIloaded) {
      clearTimeout(this.interactiveMapLoadedTimeout);
      this.map.remove();
    }

    if(this.confettiElementRemoveTimeout){
      clearTimeout(this.confettiElementRemoveTimeout);
      this.confettiElement.remove();
    }

    if (this.fullScreenChange) {
      window.document.removeEventListener(this.fullScreenChange, this.boundCheckFullscreen);
    }

    if (this.boundPlacesActivate) {
      window.removeEventListener("places-map:activate", this.boundPlacesActivate);
    }

    if (this.boundPlacesUpdate) {
      window.removeEventListener("places-map:update", this.boundPlacesUpdate);
    }

    if (this.boundMembersActivate) {
      window.removeEventListener("members-map:activate", this.boundMembersActivate);
    }

    if (this.boundMembersUpdate) {
      window.removeEventListener("members-map:update", this.boundMembersUpdate);
    }
  }

  updateMapEvents(event){
    this.log_function("updateMapEvents", event);

    this.mapEvents.push(event);
  }

  checkMapEventUsed(event){
    this.log_function("checkMapEventUsed", event);

    return this.mapEvents.includes(event);
  }
}
